import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { MasterSolutionDetailImportModel } from 'src/app/models/imports/master-solution-detail-import';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { MasterSolutionDetailModel } from 'src/app/models/preliminaries/master-solution-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { MasterSolutionDetailSelectTableModel } from 'src/app/models/tables/preliminaries/master-solution-detail-select-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MasterSolutionDetailService {

  constructor(private _httpClient: HttpClient) { }

  createAsync(masterSolutionDetailContract: MasterSolutionDetailModel): Observable<MasterSolutionDetailModel> {
    return this._httpClient.post<MasterSolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.CreateAsync}`,
      masterSolutionDetailContract);
  }

  createDetails(masterSolutionDetails: Array<MasterSolutionDetailModel>): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.CreateDetailsAsync}`,
      masterSolutionDetails);
  }

  deleteAsync(masterSolutionDetailId: number): Observable<MasterSolutionDetailModel> {
    return this._httpClient.delete<MasterSolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.DeleteAsync}/${masterSolutionDetailId}`);
  }

  deleteSomeDetailsAsync(masterSolutionDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.DeleteSomeDetailsAsync}`,
      masterSolutionDetailIds);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.ExportDetailConsultation}`, filters, {
      params,
      responseType: 'blob'
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.ExportImportErrors}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  getAllMasterSolutionDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetAllMasterSolutionDetailsWithPaginationAsync}`,
      filters, { params: params });
  }

  getArticlesForDetails(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetArticlesForDetailsAsync}`,
      filters, { params: params }
    );
  }

  getByIdAsync(masterSolutionDetailId: number): Observable<MasterSolutionDetailModel> {
    return this._httpClient.get<MasterSolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetByIdAsync}/${masterSolutionDetailId}`);
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetColumnsForDetailConsultationAsync}`);
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetColumnsForArticles}`);
  }

  getColumnsMasterSolutionDetails(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetColumnsMasterSolutionDetails}`);
  }

  getDetailsByMasterSolutionIdAsync(masterSolutionId: number): Observable<Array<MasterSolutionDetailModel>> {
    return this._httpClient.get<Array<MasterSolutionDetailModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetDetailsByMasterSolutionIdAsync}/${masterSolutionId}`);
  }

  getMasterSolutionDetailsForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], masterSolutionId: number): Observable<PagedResponseModel<MasterSolutionDetailSelectTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('masterSolutionId', masterSolutionId);
    return this._httpClient.post<PagedResponseModel<MasterSolutionDetailSelectTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetMasterSolutionDetailsForSelection}`,
      filters, { params: params }
    );
  }

  getMasterSolutionDetailByKeys(masterSolutionId: number, articleId: number): Observable<MasterSolutionDetailModel> {
    const params = new HttpParams().set('masterSolutionId', masterSolutionId).set('articleId', articleId);
    return this._httpClient.get<MasterSolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.GetMasterSolutionrDetailByKeys}`,
      { params: params }
    );
  }

  importSolutionDetails(solutionDetailImports: Array<MasterSolutionDetailImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.ImportTransferDetailsAsync}`,
      solutionDetailImports);
  }

  updateAsync(masterSolutionDetailContract: MasterSolutionDetailModel): Observable<MasterSolutionDetailModel> {
    return this._httpClient.put<MasterSolutionDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.UpdateAsync}`,
      masterSolutionDetailContract);
  }
}
